import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Address } from '../organisation.model';
import { UtilsService } from '../../../core/services/utils.service';
import { ModalService } from '../../../core/modal/modal.service';
import { AddressApiService } from '../../../core/modules/address/address-api.service';
import { Locality } from '../../../core/modules/address/address.types';

export interface AddressSelectorModalData {
  address: Address;
}

export interface AddressForm {
  currentAddress: string;
  street: string;
  house: string;
  housing: string;
  apartment: string;
  locality: Locality;
  searchText: string;
}

@Component({
  selector: 'tax-address-selector',
  templateUrl: './address-selector.component.html',
  styleUrls: ['./address-selector.component.sass'],
})
export class AddressSelectorComponent implements OnInit, OnDestroy {
  // @ts-ignore
  @Input() address: Address;

  addresses: Locality[] = [];
  // @ts-ignore
  newAddress: UntypedFormGroup;

  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    private addressApiService: AddressApiService,
    private utilsService: UtilsService,
    private fb: UntypedFormBuilder,
    private modalService: ModalService,
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  getToponym(): void {
    this.addressApiService.fetchToponyms(this.newAddress.value.searchText)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => this.addresses = response);
  }

  selectAddress(item: Locality): void {
    this.clearInput();
    this.newAddress.patchValue({
      currentAddress: item.path,
    });
    this.newAddress.patchValue({
      locality: item,
    });
  }

  closeModal(address?: Address): void {
    this.modalService.close(address);
  }

  clearInput(): void {
    this.newAddress.patchValue({
      searchText: '',
    });
    this.addresses = [];
  }

  submit(newAddress: AddressForm): void {
    const address: Address = {
      regionCode: newAddress.locality.region.code,
      city: newAddress.locality.region.title,
      housing: newAddress.housing ?? '',
      localityId: newAddress.locality.localityId,
      apartment: newAddress.apartment ?? '',
      postalCode: '',
      house: newAddress.house,
      fullAddress: (
        `${newAddress.locality.name}, ${newAddress.street} ${newAddress.house}${
          newAddress.housing ? `/${newAddress.housing}` : ''
        }${newAddress.apartment ? ` ${newAddress.apartment}` : ''}`
      ),
      region: '',
      street: newAddress.street,
      settlement: '',
    };
    this.closeModal(address);
  }

  trackByFn = () => this.utilsService.trackByFn;

  private initForm(): void {
    const currentLocality: Locality = {
      children: [],
      localityId: this.address?.localityId,
      name: this.address?.city,
      path: '',
      region: {
        // @ts-ignore
        localityId: null,
        code: this.address?.regionCode,
        title: this.address?.city,
      },
    };

    this.newAddress = this.fb.group({
      currentAddress: ['', Validators.required],
      street: [this.address?.street, Validators.required],
      house: [this.address?.house, Validators.required],
      housing: [this.address?.housing],
      apartment: [this.address?.apartment],
      locality: [currentLocality],
      searchText: [],
    });

    const locality: string = this.address?.city || this.address?.settlement;
    if (locality && this.address?.localityId) {
      this.addressApiService.fetchToponym(locality, this.address.localityId)
        .pipe(
          takeUntil(this.destroy$),
        )
        .subscribe((response) => {
          this.newAddress.patchValue({
            currentAddress: response,
          });
        });
    }
  }
}
